import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/client';
import { apiErrorText } from '@/api/opengater/errors';
import { useOgAuth } from '@/api/opengater/OgAuthContext';
import { useEventSettings, useUpdateEventSettings } from '@/api/opengater/useEventSettings';
import { PageTitle } from '@/components/PageTitle';
import { Input } from '@/components/primitives/Input';
import { SaveBar, SettingsGroup, SettingsLoading, SettingsNote, SettingsRow, } from '@/components/settings/Settings';
import s from './OpengaterEventSettings.module.css';
const CAP = 'event-settings.write';
const DECIMAL_RE = /^\d+(?:\.\d{1,2})?$/;
function draftOf(settings) {
    return {
        min_event_bonus: settings.min_event_bonus ?? '',
        max_event_bonus: settings.max_event_bonus ?? '',
        stats_refresh_minutes: String(settings.stats_refresh_minutes),
    };
}
function decimalUnits(value) {
    if (!DECIMAL_RE.test(value))
        return null;
    const [whole = '0', fraction = ''] = value.split('.');
    return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
}
function fieldValidation(draft) {
    const errors = {};
    for (const field of ['min_event_bonus', 'max_event_bonus']) {
        if (draft[field] !== '' && decimalUnits(draft[field]) === null) {
            errors[field] = 'Неотрицательное число, до 2 знаков';
        }
    }
    const minutes = Number(draft.stats_refresh_minutes);
    if (!/^\d+$/.test(draft.stats_refresh_minutes) || minutes < 1 || minutes > 1440) {
        errors.stats_refresh_minutes = 'От 1 до 1440 минут';
    }
    return errors;
}
function rangeValidation(draft) {
    if (!draft.min_event_bonus || !draft.max_event_bonus)
        return null;
    const min = decimalUnits(draft.min_event_bonus);
    const max = decimalUnits(draft.max_event_bonus);
    return min !== null && max !== null && min > max
        ? 'Минимальный бонус не может быть больше максимального'
        : null;
}
function serverValidation(error) {
    if (!(error instanceof ApiError) || error.status !== 422) {
        return { fields: {}, general: apiErrorText(error) };
    }
    const detail = error.body?.detail;
    if (typeof detail === 'string')
        return { fields: {}, general: detail };
    if (!Array.isArray(detail))
        return { fields: {}, general: apiErrorText(error) };
    const fields = {};
    const general = [];
    for (const issue of detail) {
        if (!issue || typeof issue !== 'object')
            continue;
        const { loc, msg } = issue;
        if (typeof msg !== 'string')
            continue;
        const field = Array.isArray(loc) ? loc.at(-1) : null;
        const clean = msg.replace(/^Value error,\s*/u, '');
        if (field === 'min_event_bonus' ||
            field === 'max_event_bonus' ||
            field === 'stats_refresh_minutes') {
            fields[field] = clean;
        }
        else {
            general.push(clean);
        }
    }
    return { fields, general: general.length ? general.join('; ') : null };
}
function buildPatch(base, draft, touched) {
    const patch = {};
    for (const field of touched) {
        if (draft[field] === base[field])
            continue;
        if (field === 'stats_refresh_minutes') {
            patch.stats_refresh_minutes = Number(draft[field]);
        }
        else {
            patch[field] = draft[field] === '' ? null : draft[field];
        }
    }
    return patch;
}
export function OpengaterEventSettings() {
    const { allowed, isDenied, markDenied } = useOgAuth();
    const canManage = allowed(CAP, 'admin');
    const settings = useEventSettings(canManage);
    const update = useUpdateEventSettings();
    const [base, setBase] = useState(null);
    const [draft, setDraft] = useState(null);
    const [touched, setTouched] = useState(() => new Set());
    const [serverErrors, setServerErrors] = useState({});
    const [generalError, setGeneralError] = useState(null);
    useEffect(() => {
        if (!settings.data || base)
            return;
        const incoming = draftOf(settings.data);
        setBase(incoming);
        setDraft(incoming);
    }, [base, settings.data]);
    useEffect(() => {
        if (settings.error instanceof ApiError && settings.error.status === 403)
            markDenied(CAP);
    }, [markDenied, settings.error]);
    const localErrors = draft ? fieldValidation(draft) : {};
    const errors = { ...serverErrors, ...localErrors };
    const rangeError = draft ? rangeValidation(draft) : null;
    const patch = useMemo(() => (base && draft ? buildPatch(base, draft, touched) : {}), [base, draft, touched]);
    const dirty = Object.keys(patch).length > 0;
    const canSave = dirty && !Object.keys(errors).length && !rangeError && !update.isPending;
    const change = (field, value) => {
        setDraft((current) => (current ? { ...current, [field]: value.trim() } : current));
        setTouched((current) => new Set(current).add(field));
        setServerErrors((current) => {
            if (!current[field])
                return current;
            const next = { ...current };
            delete next[field];
            return next;
        });
        setGeneralError(null);
        update.reset();
    };
    const reset = () => {
        if (!base)
            return;
        setDraft(base);
        setTouched(new Set());
        setServerErrors({});
        setGeneralError(null);
        update.reset();
    };
    const save = async (event) => {
        event.preventDefault();
        if (!canSave)
            return;
        setServerErrors({});
        setGeneralError(null);
        try {
            const saved = await update.mutateAsync(patch);
            const next = draftOf(saved);
            setBase(next);
            setDraft(next);
            setTouched(new Set());
        }
        catch (failure) {
            if (failure instanceof ApiError && failure.status === 403)
                markDenied(CAP);
            const validation = serverValidation(failure);
            setServerErrors(validation.fields);
            setGeneralError(validation.general);
        }
    };
    if (!canManage) {
        return (_jsxs(_Fragment, { children: [_jsx(PageTitle, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439", sub: "\u0411\u043E\u043D\u0443\u0441\u044B \u0438 \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u0435 \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0438" }), _jsx(SettingsNote, { tone: "lock", children: isDenied(CAP)
                        ? 'Недостаточно прав. Раздел доступен только администраторам.'
                        : 'Раздел доступен только администраторам.' })] }));
    }
    return (_jsxs("div", { className: s.content, children: [_jsx(PageTitle, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439", sub: "\u0411\u043E\u043D\u0443\u0441\u044B \u0438 \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u0435 \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0438" }), settings.isLoading && !settings.data && _jsx(SettingsLoading, {}), settings.error && !settings.data && (_jsx(SettingsNote, { tone: "error", children: apiErrorText(settings.error) })), draft && (_jsxs("form", { className: s.form, onSubmit: save, children: [rangeError && _jsx(SettingsNote, { tone: "error", children: rangeError }), generalError && _jsx(SettingsNote, { tone: "error", children: generalError }), _jsxs(SettingsGroup, { title: "\u0414\u0438\u0430\u043F\u0430\u0437\u043E\u043D \u0431\u043E\u043D\u0443\u0441\u0430", footer: "\u041F\u0443\u0441\u0442\u0430\u044F \u0433\u0440\u0430\u043D\u0438\u0446\u0430 \u0441\u043D\u0438\u043C\u0430\u0435\u0442 \u043E\u0433\u0440\u0430\u043D\u0438\u0447\u0435\u043D\u0438\u0435. \u0423\u0436\u0435 \u0441\u043E\u0437\u0434\u0430\u043D\u043D\u044B\u0435 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 \u043D\u0435 \u0438\u0437\u043C\u0435\u043D\u044F\u0442\u0441\u044F \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438.", children: [_jsx(SettingsRow, { label: "\u041C\u0438\u043D\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0431\u043E\u043D\u0443\u0441", suffix: "\u20BD", error: errors.min_event_bonus, children: _jsx(Input, { "aria-label": "\u041C\u0438\u043D\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0431\u043E\u043D\u0443\u0441", inputMode: "decimal", value: draft.min_event_bonus, disabled: update.isPending, onChange: (event) => change('min_event_bonus', event.target.value) }) }), _jsx(SettingsRow, { label: "\u041C\u0430\u043A\u0441\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0431\u043E\u043D\u0443\u0441", suffix: "\u20BD", error: errors.max_event_bonus, children: _jsx(Input, { "aria-label": "\u041C\u0430\u043A\u0441\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0431\u043E\u043D\u0443\u0441", inputMode: "decimal", value: draft.max_event_bonus, disabled: update.isPending, onChange: (event) => change('max_event_bonus', event.target.value) }) })] }), _jsx(SettingsGroup, { title: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430", children: _jsx(SettingsRow, { label: "\u041F\u0435\u0440\u0438\u043E\u0434 \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u044F", hint: "\u041A\u0430\u043A \u0434\u043E\u043B\u0433\u043E \u0441\u043D\u0438\u043C\u043E\u043A \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0438 \u0441\u0447\u0438\u0442\u0430\u0435\u0442\u0441\u044F \u0430\u043A\u0442\u0443\u0430\u043B\u044C\u043D\u044B\u043C", suffix: "\u043C\u0438\u043D.", error: errors.stats_refresh_minutes, children: _jsx(Input, { "aria-label": "\u041F\u0435\u0440\u0438\u043E\u0434 \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u044F \u0441\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0438", inputMode: "numeric", value: draft.stats_refresh_minutes, disabled: update.isPending, onChange: (event) => change('stats_refresh_minutes', event.target.value) }) }) }), _jsx(SaveBar, { dirty: dirty, saving: update.isPending, saved: update.isSuccess, canSave: canSave, error: generalError ?? undefined, onReset: reset })] }))] }));
}
